import { apiClient } from "@/api/apiClient";
import type { DtmOrthoDatasetListResponse } from "@/components/Project/dashboardImport.types";

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null;

/**
 * GET /v2/api/:siteId/dtm-ortho/dashboard-datasets の応答の形か。
 * bucket_id / type は文字列、datasets は配列、extensions はオブジェクトで、いずれも必須。
 * 要素の中身までは見ない（kind ごとの分岐は adapter が担う）
 */
export const isDtmOrthoDatasetListResponse = (
	value: unknown,
): value is DtmOrthoDatasetListResponse => {
	if (!isRecord(value)) return false;
	if (typeof value.bucket_id !== "string") return false;
	if (typeof value.type !== "string") return false;
	if (!Array.isArray(value.datasets)) return false;
	return isRecord(value.extensions);
};

/**
 * Dashboard（SC_APP バケット）の Survey 配下にある DTM / オルソのデータセット一覧と、
 * 表示ラベル用の許可拡張子を取得する（GI-18 / GI-31）。非 2xx と想定外の形は Error にする。
 *
 * @param siteId 現場 UUID
 */
export const getDtmOrthoDatasetList = async (
	siteId: string,
): Promise<DtmOrthoDatasetListResponse> => {
	const res = await apiClient.v2.api[":siteId"]["dtm-ortho"][
		"dashboard-datasets"
	].$get({ param: { siteId } });
	if (!res.ok) {
		throw new Error(`getDtmOrthoDatasetList failed: ${res.status}`);
	}
	// Keep the runtime shape check: the adapter relies on it, not on the RPC type
	const body: unknown = await res.json();
	if (!isDtmOrthoDatasetListResponse(body)) {
		throw new Error(
			"getDtmOrthoDatasetList: unexpected response shape (expected { bucket_id: string, type: string, datasets: [], extensions: {} })",
		);
	}
	return body;
};
